import React, { useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import ProfileStyle from "../../../assets/stylesheets/dashboard/profile.module.css";
import DashboardLayout from "../DashboardLayout";
import Logger from "../../../utils/logger";

const log = Logger.create("NotificationPage");

// Temporary mock notifications (until DB/backend is ready)
const MOCK_NOTIFICATIONS = [
  {
    id: "n_001",
    type: "SECURITY",
    title: "New login detected",
    message: "We noticed a new login to your account. If this wasn't you, reset your password.",
    createdAt: "2026-02-18T16:10:00.000Z",
  },
  {
    id: "n_002",
    type: "SYSTEM",
    title: "App update",
    message: "We improved performance and fixed several bugs.",
    createdAt: "2026-02-17T10:22:00.000Z",
  },
  {
    id: "n_003",
    type: "MARKETING",
    title: "New seasonal offers",
    message: "Check out new products and discounts available this week.",
    createdAt: "2026-02-16T09:00:00.000Z",
  },
  {
    id: "n_004",
    type: "SYSTEM",
    title: "Maintenance notice",
    message: "Scheduled maintenance tonight at 11:00 PM. Some features may be unavailable.",
    createdAt: "2026-02-15T20:30:00.000Z",
  },
];

const STORAGE_KEY = "farmciti.notifications.readIds.v1";

function loadReadIds() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return new Set();
    return new Set(arr);
  } catch (e) {
    log.warn("Failed to load read notification ids", e);
    return new Set();
  }
}

function persistReadIds(set) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(set)));
  } catch (e) {
    log.warn("Failed to persist read notification ids", e);
  }
}

function formatWhen(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function typeBadgeClass(type) {
  switch (type) {
    case "SECURITY":
      return ProfileStyle["notificationBadgeSecurity"];
    case "MARKETING":
      return ProfileStyle["notificationBadgeMarketing"];
    case "SYSTEM":
    default:
      return ProfileStyle["notificationBadgeSystem"];
  }
}

export default function NotificationPage() {
  // keep the same loading pattern for future backend integration
  const [isLoading] = useState(false);

  const [activeFilter, setActiveFilter] = useState("ALL"); // ALL | SECURITY | MARKETING | SYSTEM
  const [readIds, setReadIds] = useState(() => loadReadIds());

  const notifications = useMemo(() => {
    // In future: replace with data from backend
    const base = [...MOCK_NOTIFICATIONS].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    if (activeFilter === "ALL") return base;
    return base.filter((n) => n.type === activeFilter);
  }, [activeFilter]);

  const unreadCount = useMemo(() => {
    return MOCK_NOTIFICATIONS.reduce((acc, n) => (readIds.has(n.id) ? acc : acc + 1), 0);
  }, [readIds]);

  const markRead = (id) => {
    setReadIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      persistReadIds(next);
      return next;
    });
  };

  const toggleRead = (id) => {
    setReadIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      persistReadIds(next);
      return next;
    });
  };

  const markAllRead = () => {
    setReadIds((prev) => {
      const next = new Set(prev);
      for (const n of MOCK_NOTIFICATIONS) next.add(n.id);
      persistReadIds(next);
      return next;
    });
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className={ProfileStyle["loading-container"]}>
          <Loader2 className={ProfileStyle["spinner"]} size={32} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className={ProfileStyle["section"]}>
        <div className={ProfileStyle["notificationHeader"]}>
          <div>
            <h2 className={`${ProfileStyle["section-title"]} ${ProfileStyle["notificationTitle"]}`}>
              Notifications
            </h2>
            <div className={ProfileStyle["notificationSubTitle"]}>
              {unreadCount > 0 ? `${unreadCount} unread` : "All caught up"}
            </div>
          </div>

          <button
            type="button"
            className={`${ProfileStyle["save-button"]} ${ProfileStyle["notificationMarkAllBtn"]}`}
            onClick={markAllRead}
            disabled={MOCK_NOTIFICATIONS.length === 0}
          >
            Mark all as read
          </button>
        </div>

        {/* Filter pills */}
        <div className={ProfileStyle["notificationFilters"]}>
          {["ALL", "SECURITY", "MARKETING", "SYSTEM"].map((t) => {
            const active = activeFilter === t;
            return (
              <button
                key={t}
                type="button"
                onClick={() => setActiveFilter(t)}
                className={`${ProfileStyle["notificationFilterPill"]} ${
                  active ? ProfileStyle["notificationFilterPillActive"] : ""
                }`}
              >
                {t === "ALL" ? "All" : t[0] + t.slice(1).toLowerCase()}
              </button>
            );
          })}
        </div>

        {/* List */}
        <div className={ProfileStyle["notificationList"]}>
          {notifications.length === 0 ? (
            <div className={ProfileStyle["notificationEmptyCard"]}>
              <div className={ProfileStyle["notificationEmptyTitle"]}>No notifications</div>
              <div className={ProfileStyle["notificationEmptyText"]}>
                You don't have any notifications in this category yet.
              </div>
            </div>
          ) : (
            notifications.map((n) => {
              const isRead = readIds.has(n.id);

              return (
                <div
                  key={n.id}
                  onClick={() => markRead(n.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") markRead(n.id);
                  }}
                  className={`${ProfileStyle["notificationCard"]} ${
                    !isRead ? ProfileStyle["notificationCardUnread"] : ""
                  }`}
                >
                  <div className={ProfileStyle["notificationCardInner"]}>
                    <div className={ProfileStyle["notificationLeft"]}>
                      <div className={ProfileStyle["notificationBadges"]}>
                        <span
                          className={`${ProfileStyle["notificationBadge"]} ${typeBadgeClass(n.type)}`}
                        >
                          {n.type}
                        </span>

                        {!isRead ? (
                          <span className={`${ProfileStyle["notificationBadge"]} ${ProfileStyle["notificationNewBadge"]}`}>
                            NEW
                          </span>
                        ) : null}
                      </div>

                      <div className={ProfileStyle["notificationItemTitle"]}>{n.title}</div>
                      <div className={ProfileStyle["notificationMessage"]}>{n.message}</div>
                    </div>

                    <div className={ProfileStyle["notificationRight"]}>
                      <div className={ProfileStyle["notificationWhen"]}>{formatWhen(n.createdAt)}</div>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleRead(n.id);
                        }}
                        className={ProfileStyle["notificationToggleBtn"]}
                        aria-label={isRead ? "Mark as unread" : "Mark as read"}
                      >
                        {isRead ? "Unread" : "Read"}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className={ProfileStyle["notificationFooterHint"]}>
          Temporary UI: notifications are mocked on the frontend and read-state is stored in localStorage.
        </div>
      </div>
    </DashboardLayout>
  );
}